import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import memberstack from '../lib/memberstack';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MemberstackAuthContext = createContext(null);

export function MemberstackAuthProvider({ children }) {
  const [member, setMember] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Track if backend sync was successful - this is the key to preventing race conditions
  const [syncComplete, setSyncComplete] = useState(false);
  
  // Use ref to prevent duplicate auth checks
  const authCheckInProgress = useRef(false);

  // Sync member data with backend using cookies (withCredentials: true)
  const syncWithBackend = useCallback(async (memberData) => {
    const email = memberData.email || memberData.auth?.email;
    
    try {
      // withCredentials: true means the _ms-mid cookie is sent automatically
      const response = await axios.post(
        `${API}/auth/memberstack-sync`,
        { 
          memberstack_id: memberData.id,
          email: email,
          plan_connections: memberData.planConnections || []
        },
        { withCredentials: true, timeout: 10000 }
      );
      console.log('Backend sync success:', response.data);
      setSyncComplete(true);
      return response.data;
    } catch (syncError) {
      console.error('Backend sync error:', syncError.response?.status, syncError.message);
      // On sync failure, still mark as complete but log the error
      // This prevents the app from being stuck in loading state
      setSyncComplete(true); // Allow access even if sync fails - cookie auth will still work
      return null;
    }
  }, []);

  // Check auth on mount
  const checkAuth = useCallback(async () => {
    // Prevent duplicate auth checks
    if (authCheckInProgress.current) {
      console.log('Auth check already in progress, skipping');
      return;
    }
    authCheckInProgress.current = true;
    
    try {
      setLoading(true);
      setSyncComplete(false);
      
      const result = await memberstack.getCurrentMember();
      console.log('getCurrentMember:', result?.data ? 'logged in' : 'not logged in');
      
      const memberData = result?.data;
      
      if (memberData && memberData.id) {
        console.log('Member:', memberData.id, memberData.auth?.email);
        setMember(memberData);
        
        // Sync with backend but don't block on failure
        const userData = await syncWithBackend(memberData);
        if (userData) {
          setUser(userData);
          console.log('Auth check complete - user synced successfully');
        } else {
          // Even if sync fails, allow access - the cookie will authenticate API calls
          console.warn('Auth check - backend sync failed, but continuing with member data');
          setUser({
            id: memberData.id,
            email: memberData.auth?.email || '',
            memberstack_id: memberData.id,
            has_active_plan: (memberData.planConnections || []).length > 0,
          });
        }
      } else {
        setMember(null);
        setUser(null);
        setSyncComplete(false);
      }
    } catch (err) {
      console.error('Auth check error:', err);
      setMember(null);
      setUser(null);
      setSyncComplete(false);
    } finally {
      setLoading(false);
      authCheckInProgress.current = false;
    }
  }, [syncWithBackend]);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount, not when checkAuth changes

  const login = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      setSyncComplete(false);

      const result = await memberstack.openModal('LOGIN');
      console.log('Login modal completed');
      
      memberstack.hideModal();
      
      if (result?.data) {
        const memberData = result.data.member || result.data;
        setMember(memberData);
        
        // CRITICAL: Wait for backend sync to complete before returning
        console.log('Syncing with backend...');
        const userData = await syncWithBackend(memberData);
        
        if (userData) {
          setUser(userData);
          console.log('Login complete - user synced successfully');
          return { ...result, data: memberData, syncSuccess: true };
        } else {
          console.error('Login - backend sync failed');
          setUser(null);
          return { ...result, data: memberData, syncSuccess: false };
        }
      }
      
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      console.error('Login error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [syncWithBackend]);

  const signup = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      setSyncComplete(false);

      const result = await memberstack.openModal('SIGNUP');
      console.log('Signup modal completed');
      
      memberstack.hideModal();
      
      if (result?.data) {
        const memberData = result.data.member || result.data;
        setMember(memberData);
        
        // CRITICAL: Wait for backend sync to complete before returning
        console.log('Syncing with backend...');
        const userData = await syncWithBackend(memberData);
        
        if (userData) {
          setUser(userData);
          console.log('Signup complete - user synced successfully');
          return { ...result, data: memberData, syncSuccess: true };
        } else {
          console.error('Signup - backend sync failed');
          setUser(null);
          return { ...result, data: memberData, syncSuccess: false };
        }
      }
      
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Signup failed';
      setError(errorMessage);
      console.error('Signup error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [syncWithBackend]);

  const logout = useCallback(async () => {
    try {
      setError(null);
      await memberstack.logout();
      setMember(null);
      setUser(null);
      setSyncComplete(false);
      
      try {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      } catch (e) {
        // Ignore
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Logout failed';
      setError(errorMessage);
      console.error('Logout error:', err);
    }
  }, []);

  // Listen for unauthorized events (e.g., after account deletion)
  useEffect(() => {
    let unauthorizedCount = 0;
    const handleUnauthorized = async (event) => {
      unauthorizedCount++;
      console.warn('Unauthorized event received:', event.detail?.url, 'count:', unauthorizedCount);
      
      // If we get multiple 401s in quick succession, the account is likely deleted
      // Wait a bit to avoid logout on single transient errors
      if (unauthorizedCount >= 3) {
        console.log('Multiple 401 errors detected, logging out...');
        await logout();
        window.location.href = '/login?reason=session_expired';
      }
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    
    // Reset counter periodically
    const resetInterval = setInterval(() => {
      unauthorizedCount = 0;
    }, 10000); // Reset every 10 seconds
    
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      clearInterval(resetInterval);
    };
  }, [logout]);

  const hasActivePlan = useCallback(() => {
    const memberHasPlan = member?.planConnections && member.planConnections.length > 0;
    const userHasPlan = user?.has_active_plan;
    return memberHasPlan || userHasPlan;
  }, [member, user]);

  const hasPlan = useCallback((planId) => {
    if (!member?.planConnections) return false;
    return member.planConnections.some(pc => pc.planId === planId);
  }, [member]);

  return (
    <MemberstackAuthContext.Provider value={{
      // CRITICAL: isAuthenticated is ONLY true when both member exists AND backend sync succeeded
      // This prevents the race condition where API calls are made before user exists in DB
      isAuthenticated: !!member && syncComplete,
      member,
      user, 
      loading, 
      error,
      syncComplete,
      login,
      signup,
      logout,
      hasActivePlan,
      hasPlan,
      checkAuth,
    }}>
      {children}
    </MemberstackAuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(MemberstackAuthContext);
  if (!context) {
    throw new Error('useAuth must be used within MemberstackAuthProvider');
  }
  return context;
}

export { MemberstackAuthContext };
